import React, {useEffect, useRef, useState} from 'react';
import classNames from 'classnames';
import {
    Text,
    Title,
    PriorityIcon,
    Flex,
    Button,
    Input,
    Icon,
    Link,
    ButtonProperties
} from '@priority-software/priority-style-react';
import ContentLoader from 'react-content-loader';
import styles from '../styles/FormHelpDialog.module.scss';
import texts from '../texts';
import {highlightText} from '../../../_legacy/library/utils/highlightText';
import {isRTL} from '../../../Localization';
import {HyperlinkText} from '../../../components/Common/LinkTexts/LinkTexts';
import {sendMonitor} from '../../../utils/monitoring';
import {DocType} from '../types';
import {showFormHelp} from '../actions/helpActions';
import emptySearchImg from '../../../images/not-find.svg';
import {ModernTooltip, TooltipPosition} from '../../../components/Common/Tooltip/ModernTooltip';

interface HeaderProps {
    topTitle: string;
    bottomTitle?: string;
    largeHeader?: boolean;
    onBackClick?: () => void;
}

export const FormHelpHeader = ({topTitle, bottomTitle, onBackClick, largeHeader}: HeaderProps) => {
    const classes = classNames(styles.headerContainer, 'dialog-drag-handle', {
        [styles.largeHeader]: largeHeader
    });

    return (
        <div className={classes}>
            {onBackClick ? (
                <Button
                    skin="text"
                    onClick={onBackClick}
                    componentClasses={classNames(styles.blackText, styles.backButton)}
                    prefixIcon={isRTL() ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft}
                >
                    {topTitle}
                </Button>
            ) : (
                <Title skin="h6" textColor="primary-700" fontWeight="normal">
                    {topTitle}
                </Title>
            )}
            {bottomTitle && (
                <Title skin="h2" textColor="primary-500">
                    {bottomTitle}
                </Title>
            )}
        </div>
    );
};

export const TextSection = ({textData, hasXpertData}) => {
    const [showMore, setShowMore] = useState(!hasXpertData);
    const [showButton, setshowButton] = useState(false);
    const textSectionRef = useRef(null);

    const {text, linkData, onLinkClick} = textData;
    const btnText = !showMore ? texts.showMore : texts.showLess;
    const maxHeight = !showMore ? 85 : '100%';

    useEffect(() => {
        if (textSectionRef?.current?.scrollHeight > 90 && hasXpertData) {
            setshowButton(true);
        }
    }, [textData, hasXpertData]);

    return (
        <div>
            <div style={{maxHeight}} ref={textSectionRef} className={styles.textSection}>
                <Text textColor="primary-700" skin="paragraph-2">
                    <HyperlinkText text={text} linkData={linkData} onLinkClick={onLinkClick} />
                </Text>
            </div>
            <Flex justifyContent="center" componentClasses={styles.item}>
                {showButton && (
                    <Button size="small" onClick={() => setShowMore(!showMore)} skin="secondary">
                        {btnText}
                    </Button>
                )}
            </Flex>
        </div>
    );
};
export const SearchInput = ({onChange, value, onClear, placeholder}) => (
    <Input
        componentClasses={styles.searchInput}
        size="large"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
    >
        {value !== '' && <Icon icon={PriorityIcon.closeSmall} onClick={onClear} />}
        <Icon icon={PriorityIcon.searchThin} />
    </Input>
);

export const ListTitle = ({title, icon}) => {
    return (
        <Flex componentClasses={styles.section} justifyContent="space-between">
            <Flex justifyContent="flex-start">
                <Icon icon={icon} componentClasses={styles.titleIcon} />
                <Title skin="h6" textColor="primary-700">
                    {title}
                </Title>
            </Flex>
        </Flex>
    );
};

export const ListFooter = ({onViewAll = null}) => {
    return (
        <Flex componentClasses={styles.section} justifyContent="flex-end">
            {onViewAll && (
                <Button skin="text" componentClasses={styles.blueButton} onClick={onViewAll}>
                    {texts.viewAll}
                </Button>
            )}
        </Flex>
    );
};

export const Item = ({item, onClick, docType, query}) => {
    return (
        <Flex justifyContent="space-between" componentClasses={styles.item}>
            <div
                key={item.id}
                onClick={() => {
                    onClick(item.id);
                    sendMonitor(`form help - item click ${docType}`);
                }}
                className={styles.itemText}
            >
                <Text skin="subtitle-2" textColor="primary-700">
                    {highlightText(item.text, query)}
                </Text>
            </div>
            {docType === DocType.Document && (
                <Icon
                    componentClasses={styles.titleIcon}
                    icon={PriorityIcon.pdf}
                    onClick={() => window.open(item.fileUrl, '_blank')}
                />
            )}
        </Flex>
    );
};

export const VideoItem = ({item, query}) => {
    return (
        <Flex direction="column" alignItems="flex-start" componentClasses={styles.videoItem}>
            <div
                className={styles.imgContainter}
                onClick={() => {
                    sendMonitor(`form help - item click Video`);
                    window.open(`https://www.youtube.com/watch?v=${item.fileUrl}`, '_blank');
                }}
            >
                <img
                    alt=""
                    className={styles.videoImg}
                    src={`https://img.youtube.com/vi/${item.fileUrl}/mqdefault.jpg`}
                />
                <span className={styles.playIcon} />
            </div>
            <Text skin="subtitle-2" textColor="primary-700">
                {highlightText(item.text, query)}
            </Text>
        </Flex>
    );
};

export const OpenInExpert = ({onClick}) => (
    <Flex componentClasses={styles.expertButtonFooter} justifyContent="center" alignItems="center">
        <Button
            width="fullWidth"
            onClick={onClick}
            skin="text"
            componentClasses={styles.blueButton}
        >
            {texts.footerText}
        </Button>
    </Flex>
);

const EmptyStateLinkOnly = ({textBeforeLink, textAferLink, onClick = null}) => {
    const xpertLang = isRTL() ? 'he' : '';
    const text = (
        <>
            {textBeforeLink}
            <Link
                componentClasses={styles.emptyStateLink}
                onClick={
                    onClick
                        ? onClick
                        : () =>
                              window.open(
                                  `https://support.priority-software.com/${xpertLang}`,
                                  '_blank'
                              )
                }
            >
                {texts.emptyStateLink}
            </Link>
            {textAferLink}
        </>
    );
    return (
        <Text componentClasses={styles.emptyStateText} textColor="primary-700">
            {text}
        </Text>
    );
};

const EmptyState = ({title, textBeforeLink, textAferLink, onClick = null}) => {
    return (
        <Flex componentClasses={styles.section} justifyContent="center" direction="column">
            <img src={emptySearchImg} alt="" />
            <Title componentClasses={styles.emptyStateText} textColor="primary-700" skin="h4">
                {title}
            </Title>
            <EmptyStateLinkOnly
                textBeforeLink={textBeforeLink}
                textAferLink={textAferLink}
                onClick={onClick}
            />
        </Flex>
    );
};

export const EmptyStateNoData = () => {
    return (
        <EmptyState
            title={texts.emptyStateTitle}
            textBeforeLink={texts.emptyStateTextBlink}
            textAferLink={texts.emptyStateTextAlink}
        />
    );
};

export const EmptyStateXpert = props => {
    const isTextOnly: boolean = props.isTextOnly;
    if (!isTextOnly) {
        return (
            <EmptyState
                title={texts.emptyStateXpertTitle}
                textBeforeLink={texts.emptyStateTextBlink}
                textAferLink={texts.emptyStateTextAlink}
            />
        );
    } else
        return (
            <Flex
                componentClasses={styles.section}
                justifyContent="center"
                alignItems="flex-start"
                direction="column"
            >
                <EmptyStateLinkOnly
                    textBeforeLink={texts.emptyStateTextBlink}
                    textAferLink={texts.emptyStateTextAlink}
                />
            </Flex>
        );
};

export const EmptyStateSearch = ({query, onClick}) => {
    const text = texts.emptyStateSearchTitle.replace('XXX', query);
    return (
        <EmptyState
            title={text}
            textBeforeLink={texts.emptyStateSearchTextBlink}
            textAferLink={texts.emptyStateSearchTextAlink}
            onClick={onClick}
        />
    );
};

export const Loader = () => (
    <ContentLoader
        speed={5}
        interval={0.02}
        width={420}
        height={824}
        viewBox="0 0 420 824"
        backgroundColor="#ffffff"
        foregroundColor="#dee7eb"
        rtl={isRTL()}
    >
        <rect x="30" y="25" rx="3" ry="3" width="74" height="20" />
        <rect x="30" y="54" rx="3" ry="3" width="154" height="20" />
        <rect x="30" y="124" rx="3" ry="3" width="352" height="10" />
        <rect x="30" y="144" rx="3" ry="3" width="352" height="10" />
        <rect x="30" y="164" rx="3" ry="3" width="352" height="10" />
        <rect x="30" y="184" rx="3" ry="3" width="352" height="10" />
        <rect x="30" y="234" rx="3" ry="3" width="360" height="20" />
        <rect x="30" y="284" rx="3" ry="3" width="360" height="20" />
        <rect x="30" y="324" rx="3" ry="3" width="130" height="122" />
        <rect x="180" y="324" rx="3" ry="3" width="130" height="122" />
        <rect x="30" y="490" rx="3" ry="3" width="353" height="16" />
        <rect x="30" y="529" rx="3" ry="3" width="358" height="16" />
        <path d="M 30 585 h 358 v 16 H 30 z" />
        <rect x="30" y="647" rx="3" ry="3" width="351" height="16" />
        <rect x="30" y="686" rx="3" ry="3" width="358" height="16" />
        <rect x="30" y="742" rx="3" ry="3" width="358" height="16" />
        <rect x="30" y="782" rx="3" ry="3" width="358" height="16" />
        <path d="M 0 466 h 390 v 1 H 0 z M 0 623 h 390 v 1 H 0 z" />
        <rect x="330" y="324" rx="3" ry="3" width="130" height="122" />
    </ContentLoader>
);

type HeaderHelpButtonProps = {
    iconComponentClasses?: string;
    tooltipPosition?: TooltipPosition;
} & ButtonProperties;

export const HeaderHelpButton = ({
    tooltipPosition,
    iconComponentClasses
}: HeaderHelpButtonProps) => (
    <ModernTooltip tooltip={texts.helpButtonTooltip} position={tooltipPosition} arrowOffset={12}>
        <Button
            onClick={async () => {
                await showFormHelp();
                sendMonitor('form help - header help button');
            }}
            skin="text"
            prefixIcon={
                <Icon
                    componentClasses={classNames('help-icon', iconComponentClasses)}
                    icon={PriorityIcon.questionCircle}
                />
            }
        >
            <span className={styles.headerButtonText}> {texts.help}</span>
        </Button>
    </ModernTooltip>
);

export const XpertError = () => (
    <Flex componentClasses={styles.section} justifyContent="center" direction="column">
        <Title componentClasses={styles.emptyStateText} textColor="primary-500" skin="h4">
            {texts.xpertErrorTitle}
        </Title>
        <Text componentClasses={styles.emptyStateText}>{texts.xpertErrorText}</Text>
    </Flex>
);
